import { useTranslation } from "react-i18next";

import { useAllAccountingPeriods } from "@hooks/query/zenith/useAllAccountingPeriods";

/**
 * My Dashboard's accounting period: the selected scheme's active one, which
 * every card's period figures wait for. Each card calls it; they share one
 * request.
 *
 * - `accountingPeriod`: undefined while loading, between periods, or when it
 *   never loaded.
 * - `isPeriodLoading`: true until the period loads or fails.
 * - `isPeriodFetching`: true while the period is being fetched, a refresh and
 *   its retries included.
 * - `periodUnavailable`: true once a period that never loaded has failed; it
 *   stays so while retried.
 * - `periodNotice`: what each period figure says in its place; undefined while
 *   the period is current or still loading. Between periods the zones still
 *   list, but no period figure exists.
 */
export const useDashboardPeriod = () => {
  const { t } = useTranslation();
  const {
    data: accountingPeriods,
    isLoading,
    isFetching: isPeriodFetching,
    errorUpdatedAt,
  } = useAllAccountingPeriods({
    params: { isActive: true },
  });
  const accountingPeriod = accountingPeriods?.[0];
  // A failed refresh keeps the period last loaded.
  const periodUnavailable = !accountingPeriods && errorUpdatedAt > 0;
  const isPeriodLoading = isLoading && !periodUnavailable;
  const periodNotice = periodUnavailable
    ? t("zenith.dashboard.unavailable")
    : !isPeriodLoading && !accountingPeriod
      ? t("zenith.dashboard.no_active_period")
      : undefined;

  return {
    accountingPeriod,
    isPeriodLoading,
    isPeriodFetching,
    periodUnavailable,
    periodNotice,
  };
};
